import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Landmark, CheckCircle2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatIban = (v) => v.replace(/[^a-z0-9]/gi, '').toUpperCase().replace(/(.{4})/g, '$1 ').trim();

export const SepaDebitForm = ({ lookupKey, serviceName, onBack, onDone, isSubscription = false }) => {
  const [form, setForm] = useState({ name: '', email: '', iban: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/mollie/sepa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lookup_key: lookupKey,
          quantity: 1,
          customer_name: form.name,
          customer_email: form.email,
          iban: form.iban,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      setResult(data);
    } catch (err) {
      toast.error(err.message === 'IBAN invalide' ? 'IBAN invalide, vérifiez la saisie.' : 'Erreur lors du prélèvement. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div className="text-center py-4" data-testid="sepa-confirmation">
        <CheckCircle2 className="w-14 h-14 text-neon mx-auto mb-4" />
        <h3 className="text-xl font-playfair text-white mb-2">
          {result.is_subscription ? 'Abonnement activé' : 'Mandat SEPA enregistré'}
        </h3>
        <p className="text-gray-400 text-sm mb-4">
          {result.is_subscription ? (
            <>
              <span className="text-gold font-semibold">{result.amount.toFixed(2)} € / mois</span> pour{' '}
              <span className="text-white">{serviceName}</span> seront prélevés automatiquement sur le compte se terminant par{' '}
              <span className="text-white">••••{result.iban_last4}</span>. Premier prélèvement sous 2 à 5 jours ouvrés, puis chaque mois.
            </>
          ) : (
            <>
              Le prélèvement de <span className="text-gold font-semibold">{result.amount.toFixed(2)} €</span> pour{' '}
              <span className="text-white">{serviceName}</span> sera effectué sur le compte se terminant par{' '}
              <span className="text-white">••••{result.iban_last4}</span> sous 2 à 5 jours ouvrés.
            </>
          )}
        </p>
        <p className="text-xs text-gray-500 mb-6" data-testid="sepa-mandate-reference">
          Référence de mandat : {result.mandate_reference}
        </p>
        <Button onClick={onDone} className="bg-gold hover:bg-gold-light text-dark" data-testid="sepa-done-btn">
          Fermer
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="sepa-form">
      <button type="button" onClick={onBack} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gold" data-testid="sepa-back-btn">
        <ArrowLeft className="w-3 h-3" /> Autres méthodes
      </button>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gold/20 rounded-lg flex items-center justify-center">
          <Landmark className="w-5 h-5 text-gold" />
        </div>
        <div>
          <h3 className="text-white font-semibold">{isSubscription ? 'Abonnement par prélèvement SEPA' : 'Prélèvement SEPA'}</h3>
          <p className="text-xs text-gray-400">{serviceName}{isSubscription ? ' — mensuel, résiliable à tout moment' : ''}</p>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="sepa-name" className="text-gray-300">Titulaire du compte</Label>
        <Input id="sepa-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="bg-white/5 border-gold/30 text-white" placeholder="Prénom Nom" data-testid="sepa-name-input" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="sepa-email" className="text-gray-300">Email</Label>
        <Input id="sepa-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="bg-white/5 border-gold/30 text-white" placeholder="vous@exemple.com" data-testid="sepa-email-input" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="sepa-iban" className="text-gray-300">IBAN</Label>
        <Input id="sepa-iban" required value={form.iban} onChange={(e) => setForm({ ...form, iban: formatIban(e.target.value) })}
          className="bg-white/5 border-gold/30 text-white font-mono tracking-wider" placeholder="FR76 1234 5678 9012 3456 7890 123" data-testid="sepa-iban-input" />
      </div>
      <p className="text-[11px] text-gray-500 leading-relaxed">
        En validant, vous autorisez JENMA Concept et Mollie à envoyer des instructions à votre banque pour débiter votre compte
        conformément aux règles du mandat SEPA. Vous pouvez demander un remboursement dans les 8 semaines suivant le débit.
      </p>
      <Button type="submit" disabled={loading} className="w-full bg-gold hover:bg-gold-light text-dark" data-testid="sepa-submit-btn">
        {loading ? 'Enregistrement...' : isSubscription ? "Activer l'abonnement" : 'Autoriser le prélèvement'}
      </Button>
    </form>
  );
};
